import React, { useState, useCallback, useMemo, useEffect } from 'react';
import Map, { Source, Layer, Popup, Marker } from 'react-map-gl';
import * as h3 from 'h3-js';
import { useAuth } from '../contexts/AuthContext';
import { h3API, originsAPI, savedAnalysesAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import SaveAnalysisModal from '../components/SaveAnalysisModal';
import SaveAndExploreModal from '../components/SaveAndExploreModal';
import LoadAnalysisModal from '../components/LoadAnalysisModal';
import SettingsModal from '../components/SettingsModal';
import CompareAnalysisModal from '../components/CompareAnalysisModal';
import BrandBrowserModal from '../components/BrandBrowserModal';
import LocationSummaryModal from '../components/LocationSummaryModal';
import PostcodeSummaryModal from '../components/PostcodeSummaryModal';
import ConfirmationModal from '../components/ConfirmationModal';
import DestinationsSummaryModal from '../components/DestinationsSummaryModal';
import { formatVisits, formatVisitors } from '../utils/formatMetrics';
import { filterOrigins } from '../utils/filterOrigins';
import { useAppState, appStateActions, APP_STATES } from '../contexts/AppStateContext';
import './MapView.css';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const MapView = () => {
  const { user, logout } = useAuth();
  const { dispatch, currentState } = useAppState();
  const [viewState, setViewState] = useState({
    longitude: -1.5,
    latitude: 53.8,
    zoom: 6,
    pitch: 0,
    bearing: 0
  });
  const [hexData, setHexData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [colorMetric, setColorMetric] = useState('uniques'); // 'devices' or 'uniques'
  const [hoveredHex, setHoveredHex] = useState(null);
  const [selectedHex, setSelectedHex] = useState(null); // Currently selected hex for origin analysis
  const [originsData, setOriginsData] = useState(null); // Origin postcode data
  const [hoveredOrigin, setHoveredOrigin] = useState(null); // Hovered origin postcode
  const [instructionsOpen, setInstructionsOpen] = useState(true); // Instructions box state
  const [hasClickedMap, setHasClickedMap] = useState(false); // Track if user has clicked map
  const [showSaveModal, setShowSaveModal] = useState(false); // Save analysis modal
  const [showLoadModal, setShowLoadModal] = useState(false); // Load analysis modal
  const [savedAnalyses, setSavedAnalyses] = useState([]); // List of saved analyses
  const [modalLoading, setModalLoading] = useState(false); // Loading state for modal operations
  const [activeFilters, setActiveFilters] = useState([]); // Active filters for origins data
  const [showSettingsModal, setShowSettingsModal] = useState(false); // Settings modal
  const [settings, setSettings] = useState({ showTraffic: false, heatmapOpacity: 0.7 }); // User settings
  const [mapRef, setMapRef] = useState(null); // Reference to map instance
  const [showCompareModal, setShowCompareModal] = useState(false); // Compare analysis modal
  const [showBrandBrowserModal, setShowBrandBrowserModal] = useState(false); // Brand browser modal
  const [showLocationSummaryModal, setShowLocationSummaryModal] = useState(false); // Location summary modal
  const [showPostcodeSummaryModal, setShowPostcodeSummaryModal] = useState(false); // Postcode summary modal
  const [showSaveAndExploreModal, setShowSaveAndExploreModal] = useState(false); // Save and Explore modal
  const [showDestinationsSummaryModal, setShowDestinationsSummaryModal] = useState(false); // Destinations summary modal
  const [clickedPostcodeData, setClickedPostcodeData] = useState(null); // Data for clicked postcode
  const [brandLayers, setBrandLayers] = useState([]); // Array of brand location layers
  const [brandLayerVisibility, setBrandLayerVisibility] = useState({}); // Object mapping brand_id to visibility boolean
  const [hoveredBrandPin, setHoveredBrandPin] = useState(null); // Hovered brand pin
  const [destinationHexData, setDestinationHexData] = useState(null); // Destination hexes from postcode click
  const [selectedPostcode, setSelectedPostcode] = useState(null); // Currently selected postcode for destinations
  const [destinationActivityFilter, setDestinationActivityFilter] = useState('high'); // 'high' or 'all'
  const [clusterPlaceNames, setClusterPlaceNames] = useState({}); // Mapping of cluster hex IDs to place names
  const [layers, setLayers] = useState({ heatmap: false, origins: false, destinations: null }); // Layer visibility state
  // Destination map controls (thresholds stored as extrapolated visits for display consistency)
  const [minVisitorsThreshold, setMinVisitorsThreshold] = useState(20000); // Min visitors (extrapolated) for high activity filter
  const [colorWeighting, setColorWeighting] = useState('even'); // 'even' or 'lowEnd' for all activity filter
  const [clusterHexResolution, setClusterHexResolution] = useState(5); // 5 or 6 for clustered activity
  const [clusterThreshold, setClusterThreshold] = useState(200000); // Min visitors (extrapolated) for clustered activity

  // Extrapolation factor: devices * 19.2 = visits
  const VISIT_EXTRAPOLATION_FACTOR = 19.2;
  const [analysisName, setAnalysisName] = useState(null); // Current analysis name
  const [showConfirmationModal, setShowConfirmationModal] = useState(false); // New analysis confirmation modal
  const [pendingLocation, setPendingLocation] = useState(null); // Pending location for new analysis

  const handleMapClick = useCallback(async (event) => {
    const { lngLat, features } = event;

    // Close instructions on first map click
    if (!hasClickedMap) {
      setHasClickedMap(true);
      setInstructionsOpen(false);
    }

    // If clicking on features (hex or postcode), allow it
    if (features && features.length > 0) {
      const feature = features[0];

      // Check if clicking on a postcode (origin)
      if (feature.source === 'origins-data') {
        const postcodeSector = feature.properties.postcode_sector;
        console.log('Clicked on postcode:', postcodeSector);
        console.log('Feature properties:', feature.properties);

        const props = feature.properties;
        const totalPop = props.tot_pop || 1; // Avoid division by zero
        const totalProperties = props.tot_properties || 1;

        // Extract demographic data for this postcode with percentages
        // Parse spend data if it's a string (Mapbox stringifies complex objects in GeoJSON properties)
        let spendTotal = {};
        let spendPerPerson = {};

        if (props.spend_by_pcode_by_category) {
          try {
            spendTotal = typeof props.spend_by_pcode_by_category === 'string'
              ? JSON.parse(props.spend_by_pcode_by_category)
              : props.spend_by_pcode_by_category;
          } catch (e) {
            console.error('Error parsing spend_by_pcode_by_category:', e);
          }
        }

        if (props.spend_per_pers_by_category) {
          try {
            spendPerPerson = typeof props.spend_per_pers_by_category === 'string'
              ? JSON.parse(props.spend_per_pers_by_category)
              : props.spend_per_pers_by_category;
          } catch (e) {
            console.error('Error parsing spend_per_pers_by_category:', e);
          }
        }

        const postcodeData = {
          population: props.population || null,
          age: {
            'Life Stage': props.life_stage || 'Unknown'
          },
          income: {
            'Affluence': props.affluence || 'Unknown'
          },
          social_grade: {},
          tenure: {},
          employment: {},
          spend_total: spendTotal,
          spend_per_person: spendPerPerson
        };

        // Social Grade percentages
        if (props.AB_pop !== undefined) {
          postcodeData.social_grade['AB'] = (props.AB_pop / totalPop) * 100;
        }
        if (props.C1_pop !== undefined) {
          postcodeData.social_grade['C1'] = (props.C1_pop / totalPop) * 100;
        }
        if (props.C2_pop !== undefined) {
          postcodeData.social_grade['C2'] = (props.C2_pop / totalPop) * 100;
        }
        if (props.DE_pop !== undefined) {
          postcodeData.social_grade['DE'] = (props.DE_pop / totalPop) * 100;
        }

        // Tenure percentages
        if (props.owned_outright !== undefined) {
          postcodeData.tenure['Owned Outright'] = (props.owned_outright / totalProperties) * 100;
        }
        if (props.mortgage_shared !== undefined) {
          postcodeData.tenure['Mortgage/Shared'] = (props.mortgage_shared / totalProperties) * 100;
        }
        if (props.social_rented !== undefined) {
          postcodeData.tenure['Social Rented'] = (props.social_rented / totalProperties) * 100;
        }
        if (props.private_rented !== undefined) {
          postcodeData.tenure['Private Rented'] = (props.private_rented / totalProperties) * 100;
        }

        // Employment percentages (calculate based on sum of employment fields, not total population)
        const employmentFields = {
          'Part Time': 'part_time',
          'Full Time': 'full_time',
          'Self-Employed (with employees)': 'self_employed_with_employees',
          'Self-Employed (solo)': 'self_employed_solo',
          'Unemployed': 'unemployed_available',
          'Student (employed)': 'student_in_employment',
          'Student (unemployed)': 'student_unemployed_available',
          'Retired': 'inactive_retired',
          'Student': 'inactive_student',
          'Home Maker': 'inactive_looking_after_home',
          'Long-Term Sick/Disabled': 'inactive_lts_disabled',
          'Other': 'inactive_other'
        };

        // First, calculate the sum of all employment values
        const totalEmployment = Object.values(employmentFields).reduce((sum, field) => {
          return sum + (props[field] || 0);
        }, 0);

        // Then calculate percentages based on the sum (ensures they add up to 100%)
        Object.entries(employmentFields).forEach(([label, field]) => {
          if (props[field] !== undefined && props[field] > 0 && totalEmployment > 0) {
            postcodeData.employment[label] = (props[field] / totalEmployment) * 100;
          }
        });

        // Show postcode summary modal
        setSelectedPostcode(postcodeSector);
        setClickedPostcodeData(postcodeData);
        setShowPostcodeSummaryModal(true);
        dispatch(appStateActions.postcodeClicked(postcodeSector));
        return;
      }

      // If clicking on a hex, select it for origin analysis
      if (feature.source === 'hex-data' || feature.source === 'hex-3d' || feature.source === 'destination-hex-data' || feature.source === 'destination-hex-3d') {
        const hexData = {
          hex_id: feature.properties.hex_id,
          devices: feature.properties.devices,
          uniques: feature.properties.uniques
        };
        setSelectedHex(hexData);
        dispatch(appStateActions.hexClicked(hexData));
        return;
      }
    }

    // Otherwise, analyze the clicked location
    const location = { lat: lngLat.lat, lng: lngLat.lng };

    // Check if we're in an active analysis state and need confirmation
    const activeStates = [
      APP_STATES.ORIGINS_DISPLAYED,
      APP_STATES.POSTCODE_SELECTED,
      APP_STATES.LOADING_DESTINATIONS,
      APP_STATES.DESTINATIONS_LOADED
    ];

    if (activeStates.includes(currentState)) {
      // Show confirmation modal before clearing analysis
      setPendingLocation(location);
      setShowConfirmationModal(true);
      return;
    }

    // Otherwise, proceed with analyzing the location
    setLoading(true);
    setSelectedLocation(location);
    setSelectedHex(null); // Clear selected hex when clicking elsewhere
    setDestinationHexData(null); // Clear destination data
    setSelectedPostcode(null);
    setDestinationActivityFilter('high'); // Reset filter
    dispatch(appStateActions.heatmapClicked(location));

    try {
      const data = await h3API.analyzeLocation(lngLat.lat, lngLat.lng, 11);
      setHexData(data);
      // Enable heatmap layer, disable others
      setLayers({ heatmap: true, origins: false, destinations: null });
      // Set camera angle immediately
      setViewState(prev => ({
        ...prev,
        longitude: location.lng,
        latitude: location.lat,
        zoom: 10,
        pitch: 50,
        bearing: 0
      }));
    } catch (error) {
      console.error('Error analyzing location:', error);
    } finally {
      setLoading(false);
    }
  }, [hasClickedMap, currentState, dispatch]);

  // Calculate min/max for color scaling
  const { minValue, maxValue } = useMemo(() => {
    if (!hexData) return { minValue: 0, maxValue: 100 };

    const values = hexData.hexagons
      .map(hex => hex[colorMetric] || 0)
      .filter(v => v > 0); // Only consider hexes with data

    if (values.length === 0) return { minValue: 0, maxValue: 100 };

    return {
      minValue: Math.min(...values),
      maxValue: Math.max(...values)
    };
  }, [hexData, colorMetric]);

  // Convert hex data to GeoJSON for rendering
  const hexGeoJSON = hexData ? {
    type: 'FeatureCollection',
    features: hexData.hexagons.map(hex => ({
      type: 'Feature',
      geometry: hex.geometry,
      properties: {
        hex_id: hex.hex_id,
        devices: hex.devices || 0,
        uniques: hex.uniques || 0
      }
    }))
  } : null;

  // Create GeoJSON for selected hex highlight using H3 boundaries
  const selectedHexGeoJSON = useMemo(() => {
    if (!selectedHex || !selectedHex.hex_id) return null;

    try {
      // Get hex boundary from H3 - returns array of [lat, lng] pairs
      const boundary = h3.cellToBoundary(selectedHex.hex_id);

      // Convert to GeoJSON polygon format [lng, lat] and close the ring
      const coordinates = boundary.map(([lat, lng]) => [lng, lat]);
      coordinates.push(coordinates[0]); // Close the polygon

      return {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [coordinates]
          },
          properties: {
            hex_id: selectedHex.hex_id
          }
        }]
      };
    } catch (error) {
      console.error('Error creating selected hex geometry:', error);
      return null;
    }
  }, [selectedHex]);

  // Calculate center point of selected hex for pin marker
  const selectedHexCenter = useMemo(() => {
    if (!selectedHex || !selectedHex.hex_id) return null;
    try {
      const [lat, lng] = h3.cellToLatLng(selectedHex.hex_id);
      return { lat, lng };
    } catch (error) {
      console.error('Error calculating hex center:', error);
      return null;
    }
  }, [selectedHex]);

  // Convert destination hex data to GeoJSON for rendering
  const destinationHexGeoJSON = useMemo(() => {
    if (!destinationHexData || !destinationHexData.destinations) return null;

    let filteredDestinations;

    if (destinationActivityFilter === 'high') {
      // Filter to high activity only (convert extrapolated threshold back to raw devices)
      const rawThreshold = minVisitorsThreshold / VISIT_EXTRAPOLATION_FACTOR;
      filteredDestinations = destinationHexData.destinations.filter(dest => dest.total_occurences >= rawThreshold);
    } else if (destinationActivityFilter === 'clustered') {
      // Cluster hexes into larger parent hexes
      const clusterMap = new window.Map();

      destinationHexData.destinations.forEach(dest => {
        try {
          // Get parent hex at the selected resolution
          const parentHex = h3.cellToParent(dest.hex9, clusterHexResolution);

          if (clusterMap.has(parentHex)) {
            clusterMap.get(parentHex).total_occurences += dest.total_occurences;
          } else {
            clusterMap.set(parentHex, {
              hex7: parentHex,
              total_occurences: dest.total_occurences
            });
          }
        } catch (error) {
          console.warn(`Error clustering hex ${dest.hex9}:`, error);
        }
      });

      // Convert map to array and filter out clusters below threshold (convert extrapolated threshold back to raw devices)
      const rawClusterThreshold = clusterThreshold / VISIT_EXTRAPOLATION_FACTOR;
      filteredDestinations = Array.from(clusterMap.values())
        .filter(cluster => cluster.total_occurences >= rawClusterThreshold)
        .map(cluster => ({
          hex9: cluster.hex7, // Use same property name for consistency
          total_occurences: cluster.total_occurences
        }));

      // Fetch place names for cluster hexes based on resolution
      if (filteredDestinations.length > 0) {
        const clusterIds = filteredDestinations.map(dest => dest.hex9);
        const fetchPlaceNames = clusterHexResolution === 5
          ? originsAPI.getHex5PlaceNames(clusterIds)
          : originsAPI.getHex6PlaceNames(clusterIds);

        fetchPlaceNames
          .then(placeNames => {
            setClusterPlaceNames(placeNames);
          })
          .catch(error => {
            console.error(`Error fetching hex${clusterHexResolution} place names:`, error);
          });
      }
    } else {
      // Show all activity
      filteredDestinations = destinationHexData.destinations;
    }

    return {
      type: 'FeatureCollection',
      features: filteredDestinations.map(dest => {
        // Convert hex ID to geometry using h3-js
        const boundary = h3.cellToBoundary(dest.hex9); // Returns [lat, lng] pairs
        // Convert to GeoJSON format [lng, lat] and close the ring
        const coordinates = boundary.map(([lat, lng]) => [lng, lat]);
        coordinates.push(coordinates[0]); // Close the polygon

        return {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [coordinates]
          },
          properties: {
            hex_id: dest.hex9,
            devices: dest.total_occurences,
            uniques: dest.total_occurences // Using same value since we don't have separate uniques data
          }
        };
      })
    };
  }, [destinationHexData, destinationActivityFilter, minVisitorsThreshold, clusterHexResolution, clusterThreshold]);

  // Calculate min/max for destination hex color scaling
  const { minDestValue, maxDestValue } = useMemo(() => {
    if (!destinationHexData || !destinationHexData.destinations) {
      return { minDestValue: 0, maxDestValue: 100 };
    }

    // Use filtered destinations for color scaling
    let filteredDestinations;
    if (destinationActivityFilter === 'high') {
      const rawThreshold = minVisitorsThreshold / VISIT_EXTRAPOLATION_FACTOR;
      filteredDestinations = destinationHexData.destinations.filter(dest => dest.total_occurences >= rawThreshold);
    } else if (destinationActivityFilter === 'clustered') {
      // For clustered, need to recalculate the aggregated values
      const clusterMap = new window.Map();
      destinationHexData.destinations.forEach(dest => {
        try {
          const parentHex = h3.cellToParent(dest.hex9, clusterHexResolution);
          if (clusterMap.has(parentHex)) {
            clusterMap.set(parentHex, clusterMap.get(parentHex) + dest.total_occurences);
          } else {
            clusterMap.set(parentHex, dest.total_occurences);
          }
        } catch (error) {
          console.warn(`Error clustering hex ${dest.hex9}:`, error);
        }
      });
      const rawClusterThreshold = clusterThreshold / VISIT_EXTRAPOLATION_FACTOR;
      filteredDestinations = Array.from(clusterMap.values())
        .filter(total => total >= rawClusterThreshold)
        .map(total => ({ total_occurences: total }));
    } else {
      filteredDestinations = destinationHexData.destinations;
    }

    const values = filteredDestinations
      .map(dest => dest.total_occurences || 0)
      .filter(v => v > 0);

    if (values.length === 0) return { minDestValue: 0, maxDestValue: 100 };

    return {
      minDestValue: Math.min(...values),
      maxDestValue: Math.max(...values)
    };
  }, [destinationHexData, destinationActivityFilter, minVisitorsThreshold, clusterHexResolution, clusterThreshold]);

  // Calculate min/max for origins color scaling
  const { minOriginValue, maxOriginValue } = useMemo(() => {
    if (!originsData) return { minOriginValue: 0, maxOriginValue: 100 };

    const values = originsData.origins
      .map(origin => origin.total_occurences)
      .filter(v => v > 0);

    if (values.length === 0) return { minOriginValue: 0, maxOriginValue: 100 };

    return {
      minOriginValue: Math.min(...values),
      maxOriginValue: Math.max(...values)
    };
  }, [originsData]);

  // Create GeoJSON for selected postcode border (shown when destinations layer is active)
  const selectedPostcodeBorderGeoJSON = useMemo(() => {
    if (!selectedPostcode || !originsData || !layers.destinations) return null;

    // Find the selected postcode in originsData
    const postcodeOrigin = originsData.origins.find(
      origin => origin.postcode_sector === selectedPostcode
    );

    if (!postcodeOrigin || !postcodeOrigin.shape_json) return null;

    try {
      // Parse shape_json if it's a string, otherwise use as-is
      let parsed = typeof postcodeOrigin.shape_json === 'string'
        ? JSON.parse(postcodeOrigin.shape_json)
        : postcodeOrigin.shape_json;

      // Handle case where shape_json is a Feature vs just geometry
      let geometry;
      if (parsed.type === 'Feature') {
        geometry = parsed.geometry;
      } else {
        geometry = parsed;
      }

      return {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          geometry: geometry,
          properties: {
            postcode_sector: selectedPostcode
          }
        }]
      };
    } catch (error) {
      console.error('Error creating selected postcode border:', error);
      return null;
    }
  }, [selectedPostcode, originsData, layers.destinations]);

  // Convert origins data to GeoJSON for rendering
  const originsGeoJSON = useMemo(() => {
    if (!originsData) return null;

    // Apply filters to origins data
    const filteredOrigins = filterOrigins(originsData.origins, activeFilters);

    let successCount = 0;
    let failCount = 0;

    const features = filteredOrigins
      .filter(origin => origin.shape_json) // Only include origins with shapes
      .map(origin => {
        try {
          // Parse shape_json if it's a string, otherwise use as-is
          let parsed = typeof origin.shape_json === 'string'
            ? JSON.parse(origin.shape_json)
            : origin.shape_json;

          // Handle case where shape_json is a Feature (old format) vs just geometry (new format)
          let geometry;
          if (parsed.type === 'Feature') {
            geometry = parsed.geometry;
          } else {
            geometry = parsed;
          }

          // Validate geometry structure
          if (!geometry || typeof geometry !== 'object') {
            throw new Error('Geometry is not an object');
          }

          if (!geometry.type || !geometry.coordinates) {
            throw new Error('Geometry missing type or coordinates');
          }

          // Validate it's a supported geometry type
          const validTypes = ['Point', 'LineString', 'Polygon', 'MultiPoint', 'MultiLineString', 'MultiPolygon'];
          if (!validTypes.includes(geometry.type)) {
            throw new Error(`Invalid geometry type: ${geometry.type}`);
          }

          successCount++;
          return {
            type: 'Feature',
            geometry: geometry,
            properties: {
              postcode_sector: origin.postcode_sector,
              total_occurences: origin.total_occurences,
              persona_code: origin.persona_code,
              affluence: origin.affluence,
              life_stage: origin.life_stage,
              persona_share: origin.persona_share,
              country: origin.country,
              council_area_name: origin.council_area_name,
              AB_pop: origin.AB_pop,
              C1_pop: origin.C1_pop,
              C2_pop: origin.C2_pop,
              DE_pop: origin.DE_pop,
              tot_pop: origin.tot_pop,
              dominant_social_grade: origin.dominant_social_grade,
              owned_outright: origin.owned_outright,
              mortgage_shared: origin.mortgage_shared,
              social_rented: origin.social_rented,
              private_rented: origin.private_rented,
              tot_properties: origin.tot_properties,
              dominant_tenure: origin.dominant_tenure,
              imd_decile: origin.imd_decile,
              part_time: origin.part_time,
              full_time: origin.full_time,
              self_employed_with_employees: origin.self_employed_with_employees,
              self_employed_solo: origin.self_employed_solo,
              unemployed_available: origin.unemployed_available,
              student_in_employment: origin.student_in_employment,
              student_unemployed_available: origin.student_unemployed_available,
              inactive_retired: origin.inactive_retired,
              inactive_student: origin.inactive_student,
              inactive_looking_after_home: origin.inactive_looking_after_home,
              inactive_lts_disabled: origin.inactive_lts_disabled,
              inactive_other: origin.inactive_other,
              dominant_employment: origin.dominant_employment,
              no_qualifications: origin.no_qualifications,
              level_1: origin.level_1,
              level_2: origin.level_2,
              apprenticeship: origin.apprenticeship,
              level_3: origin.level_3,
              level_4: origin.level_4,
              dominant_education: origin.dominant_education,
              population: origin.population,
              spend_per_pers_by_category: origin.spend_per_pers_by_category,
              spend_by_pcode_by_category: origin.spend_by_pcode_by_category
            }
          };
        } catch (error) {
          failCount++;
          console.warn(`Skipped ${origin.postcode_sector}: ${error.message}`);
          return null;
        }
      })
      .filter(feature => feature !== null); // Remove any failed parses

    console.log(`Origins: ${successCount} loaded, ${failCount} skipped`);

    // Return null if no valid features
    if (features.length === 0) {
      console.warn('No valid origin features to display');
      return null;
    }

    return {
      type: 'FeatureCollection',
      features: features
    };
  }, [originsData, activeFilters]);

  // Handle hex and origin hover
  const handleMouseMove = useCallback((event) => {
    const { features, lngLat } = event;
    if (features && features.length > 0) {
      const feature = features[0];

      // Check if it's an origin feature
      if (feature.source === 'origins-data') {
        setHoveredOrigin({
          postcode_sector: feature.properties.postcode_sector,
          total_occurences: feature.properties.total_occurences,
          persona_code: feature.properties.persona_code,
          affluence: feature.properties.affluence,
          life_stage: feature.properties.life_stage,
          persona_share: feature.properties.persona_share,
          council_area_name: feature.properties.council_area_name,
          dominant_social_grade: feature.properties.dominant_social_grade,
          dominant_tenure: feature.properties.dominant_tenure,
          imd_decile: feature.properties.imd_decile,
          dominant_employment: feature.properties.dominant_employment,
          dominant_education: feature.properties.dominant_education,
          lng: lngLat.lng,
          lat: lngLat.lat
        });
        setHoveredHex(null);
      } else if (feature.source === 'hex-data' || feature.source === 'hex-3d' || feature.source === 'destination-hex-data' || feature.source === 'destination-hex-3d') {
        // It's a hex feature (regular or destination)
        setHoveredHex({
          hex_id: feature.properties.hex_id,
          devices: feature.properties.devices,
          uniques: feature.properties.uniques,
          lng: lngLat.lng,
          lat: lngLat.lat
        });
        setHoveredOrigin(null);
      }
    } else {
      setHoveredHex(null);
      setHoveredOrigin(null);
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredHex(null);
    setHoveredOrigin(null);
  }, []);

  // Open the save and explore modal instead of directly exploring
  const handleExploreOriginsClick = useCallback(() => {
    setShowSaveAndExploreModal(true);
  }, []);

  // Handle the actual save and explore after user enters name
  const handleSaveAndExplore = useCallback(async (name) => {
    if (!selectedHex || !selectedHex.hex_id) {
      alert('No hex selected');
      return;
    }

    // Stop idle animation

    setModalLoading(true);
    setAnalysisName(name);

    try {
      // Load origins data
      dispatch(appStateActions.startLoadingOrigins());
      const data = await originsAPI.getHexOrigins(selectedHex.hex_id);
      setOriginsData(data);

      // Enable origins layer, disable others
      setLayers({ heatmap: false, origins: true, destinations: null });
      dispatch(appStateActions.originsLoaded());

      // Calculate statistics for saving
      const hexesWithData = hexData ? hexData.hexagons.filter(h => h.devices !== null || h.uniques !== null).length : 0;
      const totalDevices = hexData ? hexData.hexagons.reduce((sum, h) => sum + (h.devices || 0), 0) : 0;
      const totalUniques = hexData ? hexData.hexagons.reduce((sum, h) => sum + (h.uniques || 0), 0) : 0;

      // Auto-save the analysis
      const saveData = {
        analysis_name: name,
        clicked_lat: selectedLocation.lat,
        clicked_lng: selectedLocation.lng,
        clicked_hex_res10: hexData.clicked_hex,
        selected_hex_id: selectedHex.hex_id,
        total_hexagons: hexData.hexagons.length,
        hexes_with_data: hexesWithData,
        total_devices: totalDevices,
        total_uniques: totalUniques,
        total_origins: data?.total_origins || null,
        hex_data: hexData,
        origins_data: data
      };

      await savedAnalysesAPI.save(saveData);
      setShowSaveAndExploreModal(false);
    } catch (error) {
      console.error('Error in save and explore:', error);
      dispatch(appStateActions.error(error.message || 'Failed to save and explore'));
      if (error.response?.data?.detail) {
        alert(error.response.data.detail);
      } else {
        alert('Failed to save and explore. Please try again.');
      }
    } finally {
      setModalLoading(false);
    }
  }, [selectedHex, selectedLocation, hexData, dispatch]);

  // Handle confirmation of new analysis when user is in active state
  const handleConfirmNewAnalysis = useCallback(async () => {
    if (!pendingLocation) return;

    // Stop idle animation

    // Clear all analysis state completely
    setOriginsData(null);
    setDestinationHexData(null);
    setSelectedPostcode(null);
    setClickedPostcodeData(null);
    setDestinationActivityFilter('high');
    setActiveFilters([]);
    setAnalysisName(null);
    setSelectedHex(null);
    setBrandLayers([]);
    setBrandLayerVisibility({});

    // Start fresh with new location
    setLoading(true);
    setSelectedLocation(pendingLocation);
    dispatch(appStateActions.heatmapClicked(pendingLocation));

    try {
      const data = await h3API.analyzeLocation(pendingLocation.lat, pendingLocation.lng, 11);
      setHexData(data);
      setLayers({ heatmap: true, origins: false, destinations: null });
      // Set camera angle immediately
      setViewState(prev => ({
        ...prev,
        longitude: pendingLocation.lng,
        latitude: pendingLocation.lat,
        zoom: 10,
        pitch: 50,
        bearing: 0
      }));
    } catch (error) {
      console.error('Error analyzing location:', error);
    } finally {
      setLoading(false);
      setPendingLocation(null);
      setShowConfirmationModal(false);
    }
  }, [pendingLocation, dispatch]);

  const handleToggleInstructions = useCallback(() => {
    setInstructionsOpen(prev => !prev);
  }, []);

  const handleToggleLayer = useCallback((layerName) => {
    // Stop idle animation on layer toggle

    // Radio-style: only one layer active at a time
    setLayers(prev => {
      if (layerName === 'heatmap') {
        dispatch(appStateActions.heatmapClicked(selectedLocation));

        // Zoom back to heatmap bounds
        if (mapRef && hexData && hexData.hexagons && hexData.hexagons.length > 0) {
          const map = mapRef.getMap();

          // Calculate bounding box of all hexagons
          let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;

          hexData.hexagons.forEach(hex => {
            if (hex.geometry && hex.geometry.coordinates && hex.geometry.coordinates.length > 0) {
              const ring = hex.geometry.coordinates[0];
              ring.forEach(coord => {
                const [lng, lat] = coord;
                minLng = Math.min(minLng, lng);
                maxLng = Math.max(maxLng, lng);
                minLat = Math.min(minLat, lat);
                maxLat = Math.max(maxLat, lat);
              });
            }
          });

          // Zoom to heatmap bounds
          if (minLng !== Infinity && maxLng !== -Infinity && minLat !== Infinity && maxLat !== -Infinity) {
            map.fitBounds(
              [[minLng, minLat], [maxLng, maxLat]],
              { padding: 50, duration: 1000, maxZoom: 16 }
            );
          }
        }

        return { heatmap: true, origins: false, destinations: null };
      } else if (layerName === 'origins') {
        dispatch(appStateActions.originsLoaded());
        // Zoom out when switching to origins layer
        setViewState(current => ({
          ...current,
          zoom: Math.max(current.zoom - 2, 4) // Zoom out 2 steps, but not below zoom level 4
        }));
        return { heatmap: false, origins: true, destinations: null };
      } else if (layerName === 'destinations') {
        dispatch(appStateActions.postcodeClicked(selectedPostcode));
        return { heatmap: false, origins: false, destinations: selectedPostcode };
      }
      return prev;
    });
  }, [dispatch, selectedLocation, selectedPostcode, mapRef, hexData]);

  const handleViewPostcodeDestinations = useCallback(async () => {
    if (!selectedPostcode) return;

    // Stop idle animation

    // Close the modal
    setShowPostcodeSummaryModal(false);

    // Load destinations
    setLoading(true);
    // Keep selectedHex so the hex/pin remains visible
    // Don't clear brand layers - they persist independently
    setActiveFilters([]);
    setDestinationActivityFilter('high'); // Reset to high activity filter
    dispatch(appStateActions.startLoadingDestinations());

    try {
      const data = await originsAPI.getPostcodeDestinations(selectedPostcode);
      console.log('Destination data:', data);
      setDestinationHexData(data);
      // Enable destinations layer, disable others
      setLayers({ heatmap: false, origins: false, destinations: selectedPostcode });
      dispatch(appStateActions.destinationsLoaded());
    } catch (error) {
      console.error('Error fetching destinations:', error);
      console.error('Error details:', error.response?.data || error.message);
      alert(`Failed to load destination data: ${error.response?.data?.detail || error.message}`);
      setDestinationHexData(null);
      dispatch(appStateActions.error(error.message || 'Failed to load destinations'));
    } finally {
      setLoading(false);
    }
  }, [selectedPostcode, dispatch]);

  const handleSelectBrand = useCallback((brandData) => {
    // Don't clear existing analysis - just add brand layer
    // Check if brand already exists
    const existingBrandIndex = brandLayers.findIndex(b => b.brand_id === brandData.brand_id);

    if (existingBrandIndex === -1) {
      // Add new brand layer
      setBrandLayers(prev => [...prev, brandData]);
      setBrandLayerVisibility(prev => ({ ...prev, [brandData.brand_id]: true }));
    } else {
      // Update existing brand layer
      setBrandLayers(prev => {
        const updated = [...prev];
        updated[existingBrandIndex] = brandData;
        return updated;
      });
      // Make it visible
      setBrandLayerVisibility(prev => ({ ...prev, [brandData.brand_id]: true }));
    }

    // Center map on first POI if available
    if (brandData.pois && brandData.pois.length > 0) {
      const firstPoi = brandData.pois[0];
      setViewState({
        longitude: firstPoi.lng,
        latitude: firstPoi.lat,
        zoom: 10
      });
    }
  }, [brandLayers]);

  const handleToggleBrandLayer = useCallback((brandId) => {
    setBrandLayerVisibility(prev => ({ ...prev, [brandId]: !prev[brandId] }));
  }, []);

  const handleDeleteBrandLayer = useCallback((brandId) => {
    setBrandLayers(prev => prev.filter(b => b.brand_id !== brandId));
    setBrandLayerVisibility(prev => {
      const updated = { ...prev };
      delete updated[brandId];
      return updated;
    });
  }, []);

  const handleBrandPinClick = useCallback(async (poi) => {
    // Clear origins data
    setOriginsData(null);
    setSelectedHex(null);

    // Analyze the pin location
    setLoading(true);
    setSelectedLocation({ lat: poi.lat, lng: poi.lng });

    try {
      const data = await h3API.analyzeLocation(poi.lat, poi.lng, 11);
      setHexData(data);
      setLayers({ heatmap: true, origins: false, destinations: null });
      // Set camera angle immediately
      setViewState(prev => ({
        ...prev,
        longitude: poi.lng,
        latitude: poi.lat,
        zoom: 10,
        pitch: 50,
        bearing: 0
      }));
    } catch (error) {
      console.error('Error analyzing location:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load saved analyses when load modal or compare modal opens
  useEffect(() => {
    if (showLoadModal || showCompareModal) {
      const fetchSavedAnalyses = async () => {
        setModalLoading(true);
        try {
          const analyses = await savedAnalysesAPI.list();
          setSavedAnalyses(analyses);
        } catch (error) {
          console.error('Error loading saved analyses:', error);
          alert('Failed to load saved analyses');
        } finally {
          setModalLoading(false);
        }
      };
      fetchSavedAnalyses();
    }
  }, [showLoadModal, showCompareModal]);

  // Control traffic layer visibility
  useEffect(() => {
    if (!mapRef) return;

    const map = mapRef.getMap();

    const updateTrafficLayers = () => {
      // Get all layers from the style
      const style = map.getStyle();
      if (!style || !style.layers) return;

      // Find all traffic-related layers
      const trafficLayers = style.layers
        .filter(layer => layer.id.includes('traffic'))
        .map(layer => layer.id);

      console.log('Found traffic layers:', trafficLayers);

      // Update visibility for all traffic layers
      trafficLayers.forEach(layerId => {
        try {
          map.setLayoutProperty(
            layerId,
            'visibility',
            settings.showTraffic ? 'visible' : 'none'
          );
        } catch (error) {
          console.warn(`Could not update layer ${layerId}:`, error);
        }
      });
    };

    // Update immediately if style is loaded
    if (map.isStyleLoaded()) {
      updateTrafficLayers();
    }

    // Also listen for style load event
    const handleLoad = () => {
      updateTrafficLayers();
    };

    map.on('load', handleLoad);
    map.on('style.load', handleLoad);

    return () => {
      map.off('load', handleLoad);
      map.off('style.load', handleLoad);
    };
  }, [mapRef, settings.showTraffic]);

  // Auto-zoom to fit hexagons when hex data is loaded
  useEffect(() => {
    if (!mapRef || !hexData || !hexData.hexagons || hexData.hexagons.length === 0) return;

    const map = mapRef.getMap();

    // Calculate bounding box of all hexagons
    let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;

    hexData.hexagons.forEach(hex => {
      if (hex.geometry && hex.geometry.coordinates && hex.geometry.coordinates.length > 0) {
        // Handle Polygon type (coordinates is array of rings)
        const ring = hex.geometry.coordinates[0];
        ring.forEach(coord => {
          const [lng, lat] = coord;
          minLng = Math.min(minLng, lng);
          maxLng = Math.max(maxLng, lng);
          minLat = Math.min(minLat, lat);
          maxLat = Math.max(maxLat, lat);
        });
      }
    });

    // Only zoom if we found valid bounds
    if (minLng !== Infinity && maxLng !== -Infinity && minLat !== Infinity && maxLat !== -Infinity) {
      map.fitBounds(
        [[minLng, minLat], [maxLng, maxLat]],
        { padding: 50, duration: 1000, maxZoom: 16 } // Limit max zoom for hex heatmap
      );
    }
  }, [mapRef, hexData]);

  // Calculate statistics for saving
  const hexesWithData = hexData ? hexData.hexagons.filter(h => h.devices !== null || h.uniques !== null).length : 0;
  const totalDevices = hexData ? hexData.hexagons.reduce((sum, h) => sum + (h.devices || 0), 0) : 0;
  const totalUniques = hexData ? hexData.hexagons.reduce((sum, h) => sum + (h.uniques || 0), 0) : 0;

  const handleSaveAnalysis = useCallback(async (analysisName) => {
    if (!hexData || !selectedLocation) {
      alert('No analysis data to save');
      return;
    }

    setModalLoading(true);
    try {
      const saveData = {
        analysis_name: analysisName,
        clicked_lat: selectedLocation.lat,
        clicked_lng: selectedLocation.lng,
        clicked_hex_res10: hexData.clicked_hex,
        selected_hex_id: selectedHex?.hex_id || null,
        total_hexagons: hexData.hexagons.length,
        hexes_with_data: hexesWithData,
        total_devices: totalDevices,
        total_uniques: totalUniques,
        total_origins: originsData?.total_origins || null,
        hex_data: hexData,
        origins_data: originsData
      };

      await savedAnalysesAPI.save(saveData);
      alert('Analysis saved successfully!');
      setShowSaveModal(false);
    } catch (error) {
      console.error('Error saving analysis:', error);
      if (error.response?.data?.detail) {
        alert(error.response.data.detail);
      } else {
        alert('Failed to save analysis. Please try again.');
      }
    } finally {
      setModalLoading(false);
    }
  }, [hexData, selectedLocation, selectedHex, originsData, hexesWithData, totalDevices, totalUniques]);

  const handleLoadAnalysis = useCallback(async (analysisId) => {
    // Stop idle animation

    setModalLoading(true);
    try {
      const analysis = await savedAnalysesAPI.load(analysisId);

      // Set the loaded data
      setSelectedLocation({
        lat: analysis.clicked_lat,
        lng: analysis.clicked_lng
      });
      setHexData(analysis.hex_data);
      setOriginsData(analysis.origins_data);
      setAnalysisName(analysis.analysis_name);

      if (analysis.selected_hex_id) {
        // Find the hex in the loaded data
        const hex = analysis.hex_data.hexagons.find(h => h.hex_id === analysis.selected_hex_id);
        if (hex) {
          setSelectedHex({
            hex_id: hex.hex_id,
            devices: hex.devices,
            uniques: hex.uniques
          });
        }
      } else {
        setSelectedHex(null);
      }

      // Update map view to center on the loaded location
      setViewState(prev => ({
        ...prev,
        longitude: analysis.clicked_lng,
        latitude: analysis.clicked_lat,
        zoom: 11
      }));

      // Set layer state based on what data is available
      if (analysis.origins_data) {
        setLayers({ heatmap: false, origins: true, destinations: null });
        dispatch(appStateActions.originsLoaded());
      } else {
        setLayers({ heatmap: true, origins: false, destinations: null });
        dispatch(appStateActions.heatmapClicked({ lat: analysis.clicked_lat, lng: analysis.clicked_lng }));
      }

      setShowLoadModal(false);
      alert('Analysis loaded successfully!');
    } catch (error) {
      console.error('Error loading analysis:', error);
      alert('Failed to load analysis. Please try again.');
    } finally {
      setModalLoading(false);
    }
  }, [dispatch]);

  const handleDeleteAnalysis = useCallback(async (analysisId) => {
    setModalLoading(true);
    try {
      await savedAnalysesAPI.delete(analysisId);
      // Remove from local list
      setSavedAnalyses(prev => prev.filter(a => a.id !== analysisId));
    } catch (error) {
      console.error('Error deleting analysis:', error);
      alert('Failed to delete analysis. Please try again.');
    } finally {
      setModalLoading(false);
    }
  }, []);

  return (
    <div className="map-view">
      <Sidebar
        user={user}
        onLogout={logout}
        hexData={hexData}
        selectedLocation={selectedLocation}
        loading={loading}
        colorMetric={colorMetric}
        onColorMetricChange={setColorMetric}
        selectedHex={selectedHex}
        analysisName={analysisName}
        onDeselectHex={() => {
          // Clear everything and return to NOTHING_SELECTED state
          setSelectedHex(null);
          setOriginsData(null);
          setAnalysisName(null);
          setDestinationHexData(null);
          setSelectedPostcode(null);
          setClickedPostcodeData(null);
          setHexData(null);
          setSelectedLocation(null);
          setActiveFilters([]);
          setBrandLayers([]);
          setBrandLayerVisibility({});
          setDestinationActivityFilter('high');
          setLayers({ heatmap: false, origins: false, destinations: null });
          dispatch(appStateActions.clearMap());
        }}
        onExploreOrigins={handleExploreOriginsClick}
        instructionsOpen={instructionsOpen}
        onToggleInstructions={handleToggleInstructions}
        onLoadAnalysis={() => setShowLoadModal(true)}
        activeFilters={activeFilters}
        onFiltersChange={setActiveFilters}
        originsData={originsData}
        onOpenSettings={() => setShowSettingsModal(true)}
        onCompareAnalysis={() => setShowCompareModal(true)}
        onLocationSummary={() => setShowLocationSummaryModal(true)}
        onOpenBrandBrowser={() => setShowBrandBrowserModal(true)}
        destinationHexData={destinationHexData}
        selectedPostcode={selectedPostcode}
        destinationActivityFilter={destinationActivityFilter}
        onDestinationActivityFilterChange={setDestinationActivityFilter}
        onDestinationsSummary={() => setShowDestinationsSummaryModal(true)}
        minVisitorsThreshold={minVisitorsThreshold}
        onMinVisitorsThresholdChange={setMinVisitorsThreshold}
        colorWeighting={colorWeighting}
        onColorWeightingChange={setColorWeighting}
        clusterHexResolution={clusterHexResolution}
        onClusterHexResolutionChange={setClusterHexResolution}
        clusterThreshold={clusterThreshold}
        onClusterThresholdChange={setClusterThreshold}
        layers={layers}
        onToggleLayer={handleToggleLayer}
        brandLayers={brandLayers}
        brandLayerVisibility={brandLayerVisibility}
        onToggleBrandLayer={handleToggleBrandLayer}
        onDeleteBrandLayer={handleDeleteBrandLayer}
      />

      <SaveAnalysisModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={handleSaveAnalysis}
        loading={modalLoading}
      />

      <SaveAndExploreModal
        isOpen={showSaveAndExploreModal}
        onClose={() => setShowSaveAndExploreModal(false)}
        onSaveAndExplore={handleSaveAndExplore}
        loading={modalLoading}
      />

      <LoadAnalysisModal
        isOpen={showLoadModal}
        onClose={() => setShowLoadModal(false)}
        onLoad={handleLoadAnalysis}
        onDelete={handleDeleteAnalysis}
        analyses={savedAnalyses}
        loading={modalLoading}
      />

      <SettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        settings={settings}
        onSettingsChange={setSettings}
      />

      <CompareAnalysisModal
        isOpen={showCompareModal}
        onClose={() => setShowCompareModal(false)}
        currentOriginsData={originsData}
        currentAnalysisName={analysisName}
        savedAnalyses={savedAnalyses}
        loading={modalLoading}
      />

      <BrandBrowserModal
        isOpen={showBrandBrowserModal}
        onClose={() => setShowBrandBrowserModal(false)}
        onSelectBrand={handleSelectBrand}
      />

      <LocationSummaryModal
        isOpen={showLocationSummaryModal}
        onClose={() => setShowLocationSummaryModal(false)}
        originsData={originsData}
        hexData={hexData}
        selectedHex={selectedHex}
        analysisName={analysisName}
      />

      <PostcodeSummaryModal
        isOpen={showPostcodeSummaryModal}
        onClose={() => setShowPostcodeSummaryModal(false)}
        postcodeData={clickedPostcodeData}
        postcodeSector={selectedPostcode}
        onViewDestinations={handleViewPostcodeDestinations}
      />

      <ConfirmationModal
        isOpen={showConfirmationModal}
        onClose={() => {
          setShowConfirmationModal(false);
          setPendingLocation(null);
        }}
        onConfirm={handleConfirmNewAnalysis}
        title="Start New Analysis?"
        message="You currently have an active analysis. Starting a new analysis will clear all current layers and data. Are you sure you want to continue?"
        confirmText="Yes, Start New Analysis"
        cancelText="No, Cancel"
      />

      <DestinationsSummaryModal
        isOpen={showDestinationsSummaryModal}
        onClose={() => setShowDestinationsSummaryModal(false)}
        destinationHexData={destinationHexData}
        clusterPlaceNames={clusterPlaceNames}
        selectedPostcode={selectedPostcode}
      />

      <div className="map-container">
        <Map
          ref={setMapRef}
          {...viewState}
          onMove={evt => setViewState(evt.viewState)}
          onClick={handleMapClick}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          mapStyle="mapbox://styles/pi-paul/clrrp755x00g201pn9lq7azdv"
          mapboxAccessToken={MAPBOX_TOKEN}
          style={{ width: '100%', height: '100%' }}
          cursor={loading ? 'wait' : 'pointer'}
          interactiveLayerIds={[
            ...(hexGeoJSON && layers.heatmap ? ['hex-fill', 'hex-extrusion'] : []),
            ...(destinationHexGeoJSON && layers.destinations ? ['destination-hex-fill', 'destination-hex-extrusion'] : []),
            ...(originsGeoJSON && layers.origins ? ['origins-fill'] : [])
          ]}
        >
          {hexGeoJSON && layers.heatmap && (
            <>
              {/* Fill layer for hex polygons */}
              <Source id="hex-data" type="geojson" data={hexGeoJSON}>
                <Layer
                  id="hex-fill"
                  type="fill"
                  paint={{
                    'fill-color': [
                      'interpolate',
                      ['linear'],
                      ['get', colorMetric],
                      minValue, '#355C7D',
                      minValue + (maxValue - minValue) * 0.07, '#496087',
                      minValue + (maxValue - minValue) * 0.14, '#5F628F',
                      minValue + (maxValue - minValue) * 0.21, '#766493',
                      minValue + (maxValue - minValue) * 0.29, '#8D6593',
                      minValue + (maxValue - minValue) * 0.36, '#A36790',
                      minValue + (maxValue - minValue) * 0.43, '#B5698A',
                      minValue + (maxValue - minValue) * 0.50, '#C36E84',
                      minValue + (maxValue - minValue) * 0.57, '#CB7686',
                      minValue + (maxValue - minValue) * 0.64, '#D37D87',
                      minValue + (maxValue - minValue) * 0.71, '#DA8689',
                      minValue + (maxValue - minValue) * 0.79, '#E18E8A',
                      minValue + (maxValue - minValue) * 0.86, '#E8968C',
                      minValue + (maxValue - minValue) * 0.93, '#ED9F8F',
                      maxValue, '#F8B195'
                    ],
                    'fill-opacity': settings.heatmapOpacity
                  }}
                />
                <Layer
                  id="hex-outline"
                  type="line"
                  paint={{
                    'line-color': '#333',
                    'line-width': 1,
                    'line-opacity': 0.3
                  }}
                />
              </Source>

              {/* 3D extrusion layer */}
              <Source id="hex-3d" type="geojson" data={hexGeoJSON}>
                <Layer
                  id="hex-extrusion"
                  type="fill-extrusion"
                  paint={{
                    'fill-extrusion-color': [
                      'interpolate',
                      ['linear'],
                      ['get', colorMetric],
                      minValue, '#355C7D',
                      minValue + (maxValue - minValue) * 0.07, '#496087',
                      minValue + (maxValue - minValue) * 0.14, '#5F628F',
                      minValue + (maxValue - minValue) * 0.21, '#766493',
                      minValue + (maxValue - minValue) * 0.29, '#8D6593',
                      minValue + (maxValue - minValue) * 0.36, '#A36790',
                      minValue + (maxValue - minValue) * 0.43, '#B5698A',
                      minValue + (maxValue - minValue) * 0.50, '#C36E84',
                      minValue + (maxValue - minValue) * 0.57, '#CB7686',
                      minValue + (maxValue - minValue) * 0.64, '#D37D87',
                      minValue + (maxValue - minValue) * 0.71, '#DA8689',
                      minValue + (maxValue - minValue) * 0.79, '#E18E8A',
                      minValue + (maxValue - minValue) * 0.86, '#E8968C',
                      minValue + (maxValue - minValue) * 0.93, '#ED9F8F',
                      maxValue, '#F8B195'
                    ],
                    'fill-extrusion-height': [
                      'interpolate',
                      ['linear'],
                      ['get', colorMetric],
                      minValue, 10,
                      maxValue, 100
                    ],
                    'fill-extrusion-base': 0,
                    'fill-extrusion-opacity': settings.heatmapOpacity + 0.1
                  }}
                />
              </Source>
            </>
          )}

          {/* Destination hexes layer (from postcode click) */}
          {destinationHexGeoJSON && layers.destinations && (
            <>
              {/* Fill layer for destination hex polygons */}
              <Source id="destination-hex-data" type="geojson" data={destinationHexGeoJSON}>
                <Layer
                  id="destination-hex-fill"
                  type="fill"
                  paint={{
                    'fill-color': destinationActivityFilter === 'clustered'
                      ? [
                          'step',
                          ['get', 'devices'],
                          '#f7fbff',  // Default (should not be visible due to filter)
                          10000, '#deebf7',  // 10k-20k
                          20000, '#c6dbef',  // 20k-30k
                          30000, '#9ecae1',  // 30k-40k
                          40000, '#6baed6',  // 40k-50k
                          50000, '#4292c6',  // 50k-60k
                          60000, '#3182bd',  // 60k-70k
                          70000, '#2171b5',  // 70k-80k
                          80000, '#08519c',  // 80k-90k
                          90000, '#08306b',  // 90k-100k
                          100000, '#041e42' // 100k+
                        ]
                      : (destinationActivityFilter === 'all' && colorWeighting === 'lowEnd')
                        ? [
                            'step',
                            ['get', 'devices'],
                            '#f7fbff',  // 0-4k visits (0-208 raw)
                            208, '#eff3ff',  // 4k-8k visits
                            417, '#e7f1ff',  // 8k-12k visits
                            625, '#deebf7',  // 12k-16k visits
                            833, '#d4e5f7',  // 16k-20k visits
                            1042, '#c6dbef',  // 20k-40k visits
                            2083, '#b3d4e8',  // 40k-60k visits
                            3125, '#9ecae1',  // 60k-80k visits
                            4167, '#8ac0da',  // 80k-100k visits
                            5208, '#74b6d3',  // 100k-120k visits
                            6250, '#6baed6',  // 120k-140k visits
                            7292, '#5ba3ce',  // 140k-160k visits
                            8333, '#4a97c6',  // 160k-180k visits
                            9375, '#4292c6',  // 180k-200k visits
                            10417, '#3182bd',  // 200k-400k visits
                            20833, '#2874b0',  // 400k-600k visits
                            31250, '#2171b5',  // 600k-800k visits
                            41667, '#1a5a9d',  // 800k-1M visits
                            52083, '#08519c',  // 1M-1.2M visits
                            62500, '#08306b'   // 1.2M+ visits
                          ]
                        : [
                            'interpolate',
                            ['linear'],
                            ['get', 'devices'],
                            minDestValue, '#f7fbff',
                            minDestValue + (maxDestValue - minDestValue) * 0.1, '#deebf7',
                            minDestValue + (maxDestValue - minDestValue) * 0.2, '#c6dbef',
                            minDestValue + (maxDestValue - minDestValue) * 0.3, '#9ecae1',
                            minDestValue + (maxDestValue - minDestValue) * 0.4, '#6baed6',
                            minDestValue + (maxDestValue - minDestValue) * 0.55, '#4292c6',
                            minDestValue + (maxDestValue - minDestValue) * 0.7, '#2171b5',
                            minDestValue + (maxDestValue - minDestValue) * 0.85, '#08519c',
                            maxDestValue, '#08306b'
                          ],
                    'fill-opacity': settings.heatmapOpacity
                  }}
                />
                <Layer
                  id="destination-hex-outline"
                  type="line"
                  paint={{
                    'line-color': '#333',
                    'line-width': 1,
                    'line-opacity': 0.3
                  }}
                />
              </Source>

              {/* 3D extrusion layer for destination hexes */}
              <Source id="destination-hex-3d" type="geojson" data={destinationHexGeoJSON}>
                <Layer
                  id="destination-hex-extrusion"
                  type="fill-extrusion"
                  paint={{
                    'fill-extrusion-color': destinationActivityFilter === 'clustered'
                      ? [
                          'step',
                          ['get', 'devices'],
                          '#ffffcc',  // Default (should not be visible due to filter)
                          10000, '#ffeda0',  // 10k-20k
                          20000, '#fed976',  // 20k-30k
                          30000, '#feb24c',  // 30k-40k
                          40000, '#fd8d3c',  // 40k-50k
                          50000, '#fc4e2a',  // 50k-60k
                          60000, '#f03b20',  // 60k-70k
                          70000, '#e31a1c',  // 70k-80k
                          80000, '#bd0026',  // 80k-90k
                          90000, '#800026',  // 90k-100k
                          100000, '#67001f'  // 100k+
                        ]
                      : (destinationActivityFilter === 'all' && colorWeighting === 'lowEnd')
                        ? [
                            'step',
                            ['get', 'devices'],
                            '#ffffcc',  // 0-4k visits (0-208 raw)
                            208, '#fff7bc',  // 4k-8k visits
                            417, '#ffefa5',  // 8k-12k visits
                            625, '#ffeda0',  // 12k-16k visits
                            833, '#ffe68f',  // 16k-20k visits
                            1042, '#fed976',  // 20k-40k visits
                            2083, '#fed460',  // 40k-60k visits
                            3125, '#feb24c',  // 60k-80k visits
                            4167, '#fea03d',  // 80k-100k visits
                            5208, '#fe8d2e',  // 100k-120k visits
                            6250, '#fd8d3c',  // 120k-140k visits
                            7292, '#fc7030',  // 140k-160k visits
                            8333, '#fc5b25',  // 160k-180k visits
                            9375, '#fc4e2a',  // 180k-200k visits
                            10417, '#f03b20',  // 200k-400k visits
                            20833, '#e31a1c',  // 400k-600k visits
                            31250, '#d40f18',  // 600k-800k visits
                            41667, '#bd0026',  // 800k-1M visits
                            52083, '#a50024',  // 1M-1.2M visits
                            62500, '#800026'   // 1.2M+ visits
                          ]
                        : [
                            'interpolate',
                            ['linear'],
                            ['get', 'devices'],
                            minDestValue, '#ffffcc',
                            minDestValue + (maxDestValue - minDestValue) * 0.1, '#ffeda0',
                            minDestValue + (maxDestValue - minDestValue) * 0.2, '#fed976',
                            minDestValue + (maxDestValue - minDestValue) * 0.3, '#feb24c',
                            minDestValue + (maxDestValue - minDestValue) * 0.4, '#fd8d3c',
                            minDestValue + (maxDestValue - minDestValue) * 0.55, '#fc4e2a',
                            minDestValue + (maxDestValue - minDestValue) * 0.7, '#e31a1c',
                            minDestValue + (maxDestValue - minDestValue) * 0.85, '#bd0026',
                            maxDestValue, '#800026'
                          ],
                    'fill-extrusion-height': destinationActivityFilter === 'clustered'
                      ? [
                          'step',
                          ['get', 'devices'],
                          20,  // Default height
                          10000, 30,   // 10k-20k
                          20000, 40,   // 20k-30k
                          30000, 50,   // 30k-40k
                          40000, 60,   // 40k-50k
                          50000, 70,   // 50k-60k
                          60000, 80,   // 60k-70k
                          70000, 90,   // 70k-80k
                          80000, 100,  // 80k-90k
                          90000, 110,  // 90k-100k
                          100000, 120  // 100k+
                        ]
                      : [
                          'interpolate',
                          ['linear'],
                          ['get', 'devices'],
                          minDestValue, 10,
                          maxDestValue, 100
                        ],
                    'fill-extrusion-base': 0,
                    'fill-extrusion-opacity': settings.heatmapOpacity + 0.1
                  }}
                />
              </Source>
            </>
          )}

          {/* Selected postcode sector border (shown when destinations layer is active) */}
          {selectedPostcodeBorderGeoJSON && layers.destinations && (
            <Source id="selected-postcode-border" type="geojson" data={selectedPostcodeBorderGeoJSON}>
              <Layer
                id="selected-postcode-border-outline"
                type="line"
                paint={{
                  'line-color': '#ffffff',
                  'line-width': 3,
                  'line-opacity': 1
                }}
                layout={{
                  'line-cap': 'round',
                  'line-join': 'round'
                }}
              />
            </Source>
          )}

          {/* Origin postcodes layer */}
          {originsGeoJSON && layers.origins && (
            <Source id="origins-data" type="geojson" data={originsGeoJSON}>
              <Layer
                id="origins-fill"
                type="fill"
                paint={{
                  'fill-color': [
                    'interpolate',
                    ['linear'],
                    ['get', 'total_occurences'],
                    minOriginValue, '#2C1E3D',
                    minOriginValue + (maxOriginValue - minOriginValue) * 0.1, '#3A2747',
                    minOriginValue + (maxOriginValue - minOriginValue) * 0.2, '#4A315A',
                    minOriginValue + (maxOriginValue - minOriginValue) * 0.3, '#5A3B6D',
                    minOriginValue + (maxOriginValue - minOriginValue) * 0.4, '#6A4570',
                    minOriginValue + (maxOriginValue - minOriginValue) * 0.5, '#7A4F73',
                    minOriginValue + (maxOriginValue - minOriginValue) * 0.6, '#8A5976',
                    minOriginValue + (maxOriginValue - minOriginValue) * 0.7, '#9A6379',
                    minOriginValue + (maxOriginValue - minOriginValue) * 0.8, '#AA6D7C',
                    minOriginValue + (maxOriginValue - minOriginValue) * 0.9, '#BA777F',
                    maxOriginValue, '#EDD1CA'
                  ],
                  'fill-opacity': 0.7
                }}
              />
              <Layer
                id="origins-outline"
                type="line"
                paint={{
                  'line-color': [
                    'case',
                    ['==', ['get', 'imd_decile'], null], '#ffffff',
                    [
                      'interpolate',
                      ['linear'],
                      ['get', 'imd_decile'],
                      1, '#7F0000',  // Decile 1 - dark red (most deprived)
                      2, '#AB0604',
                      3, '#CE2619',
                      4, '#E65339',
                      5, '#F67B52',
                      6, '#FDC28C',
                      7, '#FDC28C',
                      8, '#FDD8A9',
                      9, '#FEE9CB',
                      10, '#FFF7EC'  // Decile 10 - cream (least deprived)
                    ]
                  ],
                  'line-width': 2,
                  'line-opacity': 0.9
                }}
              />
            </Source>
          )}

          {/* Selected hex highlight - always visible when zoom >= 13 */}
          {selectedHexGeoJSON && viewState.zoom >= 13 && (
            <Source id="selected-hex" type="geojson" data={selectedHexGeoJSON}>
              <Layer
                id="selected-hex-outline"
                type="line"
                paint={{
                  'line-color': '#ffff00',
                  'line-width': 4,
                  'line-opacity': 1
                }}
                layout={{
                  'line-cap': 'round',
                  'line-join': 'round'
                }}
              />
            </Source>
          )}

          {/* Green pin marker for selected hex when zoomed out < 13 */}
          {selectedHexCenter && viewState.zoom < 13 && (
            <Marker
              longitude={selectedHexCenter.lng}
              latitude={selectedHexCenter.lat}
              anchor="bottom"
            >
              <div style={{
                width: '30px',
                height: '30px',
                backgroundColor: '#00ff00',
                border: '3px solid #ffffff',
                borderRadius: '50% 50% 50% 0',
                transform: 'rotate(-45deg)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                cursor: 'pointer'
              }} />
            </Marker>
          )}

          {/* Hover tooltip for hexes */}
          {hoveredHex && (
            <Popup
              longitude={hoveredHex.lng}
              latitude={hoveredHex.lat}
              closeButton={false}
              closeOnClick={false}
              anchor="bottom"
              offset={[0, -10]}
            >
              <div style={{ padding: '8px', minWidth: '150px' }}>
                {/* Show place name if this is a clustered destination */}
                {destinationActivityFilter === 'clustered' && clusterPlaceNames[hoveredHex.hex_id] && (
                  <div style={{ marginBottom: '8px', fontWeight: 'bold', fontSize: '14px', color: '#3498db' }}>
                    {clusterPlaceNames[hoveredHex.hex_id]}
                  </div>
                )}
                <div><strong>Visits:</strong> {formatVisits(hoveredHex.devices)}</div>
                <div><strong>Visitors:</strong> {formatVisitors(hoveredHex.uniques)}</div>
              </div>
            </Popup>
          )}

          {/* Hover tooltip for origins */}
          {hoveredOrigin && (
            <Popup
              longitude={hoveredOrigin.lng}
              latitude={hoveredOrigin.lat}
              closeButton={false}
              closeOnClick={false}
              anchor="bottom"
              offset={[0, -10]}
            >
              <div style={{ padding: '10px', minWidth: '200px' }}>
                <div style={{ marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
                  {hoveredOrigin.postcode_sector}
                </div>
                {hoveredOrigin.council_area_name && (
                  <div style={{ marginBottom: '4px', fontSize: '12px', color: '#999' }}>
                    {hoveredOrigin.council_area_name}
                  </div>
                )}
                <div style={{ marginBottom: '4px' }}>
                  <strong>Visits:</strong> {formatVisits(hoveredOrigin.total_occurences)}
                </div>
                <div style={{ marginBottom: '4px' }}>
                  <strong>Profile:</strong> {hoveredOrigin.persona_code}
                  {hoveredOrigin.persona_share && (
                    <span style={{ marginLeft: '4px', fontSize: '11px', color: '#888' }}>
                      ({(hoveredOrigin.persona_share * 100).toFixed(1)}%)
                    </span>
                  )}
                </div>
                <div style={{ fontStyle: 'italic', color: '#666', fontSize: '12px', marginBottom: '4px' }}>
                  {hoveredOrigin.affluence} {hoveredOrigin.life_stage}
                </div>
                {hoveredOrigin.dominant_social_grade && (
                  <div style={{ marginTop: '6px', fontSize: '12px' }}>
                    <strong>Social Grade:</strong> {hoveredOrigin.dominant_social_grade}
                  </div>
                )}
                {hoveredOrigin.dominant_tenure && (
                  <div style={{ fontSize: '12px' }}>
                    <strong>Tenure:</strong> {hoveredOrigin.dominant_tenure}
                  </div>
                )}
                {hoveredOrigin.imd_decile && (
                  <div style={{ fontSize: '12px' }}>
                    <strong>IMD Decile:</strong> {hoveredOrigin.imd_decile}
                  </div>
                )}
                {hoveredOrigin.dominant_employment && (
                  <div style={{ fontSize: '12px' }}>
                    <strong>Employment:</strong> {hoveredOrigin.dominant_employment}
                  </div>
                )}
                {hoveredOrigin.dominant_education && (
                  <div style={{ fontSize: '12px' }}>
                    <strong>Education:</strong> {hoveredOrigin.dominant_education}
                  </div>
                )}
              </div>
            </Popup>
          )}

          {/* Brand location pins - iterate through all brand layers */}
          {brandLayers.map(brand => (
            brandLayerVisibility[brand.brand_id] && brand.pois && brand.pois.map((poi, index) => (
              <Marker
                key={`${brand.brand_id}-${poi.postcode}-${index}`}
                longitude={poi.lng}
                latitude={poi.lat}
                anchor="bottom"
                onClick={(e) => {
                  e.originalEvent.stopPropagation();
                  handleBrandPinClick(poi);
                }}
              >
                <div
                  onMouseEnter={() => setHoveredBrandPin({ poi, brandName: brand.brand_name })}
                  onMouseLeave={() => setHoveredBrandPin(null)}
                  style={{
                    cursor: 'pointer',
                    fontSize: '24px',
                    transform: hoveredBrandPin?.poi?.postcode === poi.postcode ? 'scale(1.2)' : 'scale(1)',
                    transition: 'transform 0.2s'
                  }}
                >
                  📍
                </div>
              </Marker>
            ))
          ))}

          {/* Hover tooltip for brand pins */}
          {hoveredBrandPin && (
            <Popup
              longitude={hoveredBrandPin.poi.lng}
              latitude={hoveredBrandPin.poi.lat}
              closeButton={false}
              closeOnClick={false}
              anchor="bottom"
              offset={[0, -30]}
            >
              <div style={{ padding: '10px', minWidth: '200px' }}>
                <div style={{ marginBottom: '6px', fontWeight: 'bold', fontSize: '14px' }}>
                  {hoveredBrandPin.brandName}
                </div>
                {hoveredBrandPin.poi.address && (
                  <div style={{ fontSize: '12px', marginBottom: '2px' }}>
                    {hoveredBrandPin.poi.address}
                  </div>
                )}
                {hoveredBrandPin.poi.locality && (
                  <div style={{ fontSize: '12px', marginBottom: '2px' }}>
                    {hoveredBrandPin.poi.locality}
                  </div>
                )}
                {hoveredBrandPin.poi.postcode && (
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {hoveredBrandPin.poi.postcode}
                  </div>
                )}
              </div>
            </Popup>
          )}
        </Map>
      </div>
    </div>
  );
};

export default MapView;
